import * as R from 'ramda'

import {
  RugbyLeagueAttributeId,
  RugbyLeagueEventStatusId,
  RugbyLeagueIncidentId,
} from '@collector/sportsapi-client-legacy'

import {
  BlockedIncidentsMap,
  getPitchIncidentIds,
  PitchIncidents,
  PitchIncidentsConfig,
} from '../../../configuration'

const pitchIncidents: PitchIncidents<
  RugbyLeagueIncidentId,
  RugbyLeagueAttributeId
> = {
  gridClass: 'grid-cols-12 @2md:grid-cols-6',
  incidentButtonData: [
    {
      incidentId: RugbyLeagueIncidentId.Try,
      class: 'col-start-1 col-span-6 row-1 @2md:col-span-6',
    },
    {
      incidentId: RugbyLeagueIncidentId.ConversionAttempt,
      class: 'col-span-6 row-1 @2md:col-span-3 @2md:row-3',
    },
    {
      incidentId: RugbyLeagueIncidentId.DropGoal,
      class: 'col-span-4 row-2 @2md:col-span-3 @2md:row-2',
    },
    {
      incidentId: RugbyLeagueIncidentId.DropGoalMissed,
      class: 'col-span-4 row-2 @2md:col-span-3 @2md:row-2',
    },
    {
      incidentId: RugbyLeagueIncidentId.PenaltyAwarded,
      class: 'col-span-4 row-2 @2md:col-span-3 @2md:row-3',
    },
    {
      incidentId: RugbyLeagueIncidentId.Offload,
      class: 'col-span-3 row-3 @2md:col-span-3 @2md:row-5',
      name: 'Off\u200Bload',
    },
    {
      incidentId: RugbyLeagueIncidentId.GoallineDropout,
      class: 'col-span-3 row-3 @2md:col-span-3 @2md:row-4',
      name: 'Goal\u200Bline drop',
    },
    {
      incidentId: RugbyLeagueIncidentId.Turnover,
      class: 'col-span-3 row-3 @2md:col-span-2 @2md:row-7',
      name: 'Turn\u200Bover',
    },
    {
      incidentId: RugbyLeagueIncidentId.Kick,
      class: 'col-span-3 row-3 @2md:col-span-3 @2md:row-5',
    },
    {
      incidentId: RugbyLeagueIncidentId.Foul,
      class: 'col-span-3 row-4 @2md:col-span-3 @2md:row-6',
    },
    {
      incidentId: RugbyLeagueIncidentId.FreeKick,
      class: 'col-span-3 row-4 @2md:col-span-3 @2md:row-6',
      name: 'Free Kick',
    },
    {
      incidentId: RugbyLeagueIncidentId.Scrum,
      class: 'col-span-3 row-4 @2md:col-span-2 @2md:row-7',
    },
    {
      incidentId: RugbyLeagueIncidentId.TackleBreak,
      class: 'col-span-3 row-4 @2md:col-span-3 @2md:row-4',
    },
    {
      incidentId: RugbyLeagueIncidentId.PenaltyTry,
      class: 'col-span-3 row-5 @2md:col-span-2 @2md:row-8',
      name: 'Pena\u200Blty\u00a0try',
    },
    {
      incidentId: RugbyLeagueIncidentId.Cards,
      class: 'col-span-3 row-5 @2md:col-span-2 @2md:row-8',
      name: 'Cards',
      onClick: async (_, opts) => {
        if (!opts) return
        const { incidentId, participantTeamId, setIncident } = opts
        await setIncident({
          incidentId,
          participantTeamId,
        })
      },
    },
    {
      incidentId: RugbyLeagueIncidentId.SubstitutionIn,
      class: 'col-span-3 row-5 @2md:col-span-2 @2md:row-8',
      name: 'Subs',
    },
    {
      incidentId: RugbyLeagueIncidentId.Lineout,
      class: 'col-span-3 row-5 @2md:col-span-2 @2md:row-7',
      name: 'Line\u200Bout',
    },
  ],
}
const eventStatusToBlockedPitchIncidentsTextMap = new Map<
  RugbyLeagueEventStatusId,
  string
>([
  [RugbyLeagueEventStatusId.NotStarted, 'start the match'],
  [RugbyLeagueEventStatusId.StartDelayed, 'start the match'],
  [RugbyLeagueEventStatusId.WaitingForPenalty, 'start the penalty shootout'],
])

const pitchIncidentIds = getPitchIncidentIds(pitchIncidents.incidentButtonData)

const pitchIncidentIdsWithoutSubstitutionIn: Set<RugbyLeagueIncidentId> =
  new Set(
    R.without([RugbyLeagueIncidentId.SubstitutionIn], [...pitchIncidentIds]),
  )

const blockedPitchIncidentsMap: BlockedIncidentsMap<
  RugbyLeagueEventStatusId,
  RugbyLeagueIncidentId
> = new Map([
  [RugbyLeagueEventStatusId.Abandoned, pitchIncidentIds],
  [RugbyLeagueEventStatusId.Cancelled, pitchIncidentIds],
  [RugbyLeagueEventStatusId.NotStarted, pitchIncidentIds],
  [RugbyLeagueEventStatusId.StartDelayed, pitchIncidentIds],
  [RugbyLeagueEventStatusId.Postponed, pitchIncidentIds],
  [RugbyLeagueEventStatusId.Halftime, pitchIncidentIdsWithoutSubstitutionIn],
  [
    RugbyLeagueEventStatusId.WaitingForExtratime,
    pitchIncidentIdsWithoutSubstitutionIn,
  ],
  [
    RugbyLeagueEventStatusId.ExtratimeHalftime,
    pitchIncidentIdsWithoutSubstitutionIn,
  ],
  [RugbyLeagueEventStatusId.WaitingForPenalty, pitchIncidentIds],
  [
    RugbyLeagueEventStatusId.Penalties,
    new Set(
      R.without(
        [
          RugbyLeagueIncidentId.PenaltyKickGoal,
          RugbyLeagueIncidentId.PenaltyKickMissed,
          RugbyLeagueIncidentId.PenaltyAwarded,
        ],
        [...pitchIncidentIds],
      ),
    ),
  ],
  [RugbyLeagueEventStatusId.FinishedAfterExtratime, pitchIncidentIds],
  [RugbyLeagueEventStatusId.FinishedAfterPenalties, pitchIncidentIds],
  [RugbyLeagueEventStatusId.FinishedAwardedWin, pitchIncidentIds],
  [RugbyLeagueEventStatusId.FinishedRegularTime, pitchIncidentIds],
  [RugbyLeagueEventStatusId.Interrupted, pitchIncidentIds],
  [RugbyLeagueEventStatusId.ToFinish, pitchIncidentIds],
])

export const pitchIncidentsConfig: PitchIncidentsConfig<
  RugbyLeagueEventStatusId,
  RugbyLeagueIncidentId,
  RugbyLeagueAttributeId
> = {
  pitchIncidents,
  blockedPitchIncidentsMap,
  eventStatusToBlockedPitchIncidentsTextMap,
}
